import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import GradientBorderBox from "../../../../uiComponents/GradiendBorderBox";
import DownArrow from "../../../../../../../../assets/images/DownArrowIcon.svg";
import BackIcon from "../../../../../../../../assets/images/BackIcon.svg";
import CloseIcon from "../../../../../../../../assets/images/CloseIcon.svg";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import {
  buildMapiCurrencyOptionsFromTransactions,
  getMapiQuoteTransactions,
} from "../../../../../helper/mapiQuoteResponseHelpers";

countries.registerLocale(enLocale);

type CurrencyOption = {
  currency: string;
  rate: number;
};

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  onCurrencyChange?: (currency: string, rate: number) => void;
  exchangeRate?: number;
  receiveCurrency?: string;
  isQuoteLoading?: boolean;
  testID?: string | undefined;
};

export default function MapiSendAmountEntryStep({
  onValidationChange,
  onCurrencyChange,
  exchangeRate = 1,
  receiveCurrency = "",
  isQuoteLoading = false,
  testID,
}: Props) {
  const { t } = useTranslation();
  const sendAmount = useSessionStore(
    (s: SessionState) => s.currentSendAmount
  );
  const receiveAmount = useSessionStore(
    (s: SessionState) => s.theyReceiveAmount
  );
  const selectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode
  );
  const setCurrentSendAmount = useSessionStore(
    (s: SessionState) => (s as any).setCurrentSendAmount
  );
  const setTheyReceiveAmount = useSessionStore(
    (s: SessionState) => (s as any).setTheyReceiveAmount
  );
  const mapiQuoteData = useSessionStore(
    (s: SessionState) => (s as any).mapiQuoteResponse
  );
  const currencyOptions: CurrencyOption[] = useMemo(
    () =>
      buildMapiCurrencyOptionsFromTransactions(
        getMapiQuoteTransactions(mapiQuoteData)
      ),
    [mapiQuoteData]
  );

  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [localRate, setLocalRate] = useState<number>(exchangeRate);
  const [localCurrency, setLocalCurrency] = useState<string>(receiveCurrency);

  const alpha2 = useMemo(() => {
    const a3 = (selectedDestinationCountryCode || "")
      .toString()
      .toUpperCase();
    return countries.alpha3ToAlpha2(a3) || "";
  }, [selectedDestinationCountryCode]);

  useEffect(() => {
    setLocalRate(exchangeRate);
    setLocalCurrency(receiveCurrency);
    setSelectedCurrency(receiveCurrency || null);
  }, [exchangeRate, receiveCurrency]);

  const sendValue = parseFloat(sendAmount || "0");
  const isValid = sendAmount !== "0.00" && sendAmount !== "" && sendValue > 0;
  const showConversion = isValid && localRate > 0 && localCurrency !== "";

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  useEffect(() => {
    if (sendAmount && localRate > 0) {
      setTheyReceiveAmount(computeReceiveFromSend(sendAmount, localRate));
    }
  }, [localRate]);

  const parseAmount = (text: string): string => {
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const computeReceiveFromSend = (
    sendStr: string | undefined,
    rate: number
  ) => {
    const s = parseFloat(sendStr || "0");
    if (!isFinite(s)) return "0.00";
    return (s * (rate || 0)).toFixed(2);
  };

  const computeSendFromReceive = (recvStr: string, rate: number) => {
    const r = parseFloat(recvStr || "0");
    if (!isFinite(r) || !rate) return "0.00";
    return (r / rate).toFixed(2);
  };

  const handleCurrencySelect = (opt: CurrencyOption) => {
    setSelectedCurrency(opt.currency);
    setLocalCurrency(opt.currency);
    setLocalRate(opt.rate);
    setTheyReceiveAmount(computeReceiveFromSend(sendAmount, opt.rate));
    onCurrencyChange?.(opt.currency, opt.rate);
    setShowCurrencyModal(false);
  };

  return (
    <View style={styles.container} testID={testID}>
      {showConversion && (
        <Text style={styles.title}>
          1 USD = {localRate.toFixed(4)} {localCurrency}
        </Text>
      )}
      <Text style={styles.youSendTitle}>
        {t("YOU_SEND")}
      </Text>
      <View
        style={[
          styles.amountInput,
          isQuoteLoading ? { opacity: 0.5 } : undefined,
        ]}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <View style={styles.shrinkWrap}>
            <TextInput
              testID="mapiSendAmount"
              autoFocus
              style={styles.amountText}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={Colors.subTitleColor}
              editable={!isQuoteLoading}
              value={sendAmount}
              onChangeText={(t) => {
                const next = parseAmount(t);
                setCurrentSendAmount?.(next);
                setTheyReceiveAmount(
                  computeReceiveFromSend(next, localRate)
                );
              }}
            />
          </View>
          <View
            style={{
              marginLeft: 8,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {Platform.OS === "web" ? (
              <View style={styles.flagOverflow}>
                <ReactCountryFlag
                  countryCode="US"
                  svg
                  style={{ width: 24, height: 24, borderRadius: 50 }}
                  aria-label="USD"
                />
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🇺🇸</Text>
            )}
            <Text style={styles.currencyText}>USD</Text>
          </View>
        </View>
      </View>
      <Text style={[styles.youSendTitle, { marginTop: 24 }]}>
        {t("THEY_RECEIVE")}
      </Text>
      <View
        style={[
          styles.amountInput,
          { marginBottom: 48 },
          isQuoteLoading ? { opacity: 0.5 } : undefined,
        ]}
      >
        <TextInput
          testID="mapiReceiveAmount"
          style={styles.amountText}
          keyboardType="decimal-pad"
          placeholder="0.00"
          placeholderTextColor={Colors.subTitleColor}
          editable={!isQuoteLoading}
          value={receiveAmount}
          onChangeText={(t) => {
            const next = parseAmount(t);
            setTheyReceiveAmount(next);
            setCurrentSendAmount(
              computeSendFromReceive(next, localRate)
            );
          }}
        />
        <TouchableOpacity
          disabled={currencyOptions.length <= 1 || isQuoteLoading}
          onPress={() => setShowCurrencyModal(true)}
          testID="mapiReceiveCurrencyButton"
        >
          <View
            style={{
              marginLeft: 8,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {Platform.OS === "web" && alpha2 ? (
              <View style={styles.flagOverflow}>
                <ReactCountryFlag
                  countryCode={alpha2}
                  svg
                  style={{ width: 24, height: 24, borderRadius: 50 }}
                  aria-label={localCurrency || ""}
                />
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )}
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={styles.currencyText}>
                {localCurrency || "—"}
              </Text>
              {currencyOptions.length > 1 && (
                <DownArrow width={16} height={16} />
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>
      <GradientBorderBox>
        <Text style={styles.subtitle}>{t("AMOUNT_ENTRY_SUBTITLE")}</Text>
      </GradientBorderBox>

      {/* choose currency modal for receive currency */}
      <Modal visible={showCurrencyModal} animationType="slide" transparent={true}>
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.modalHeaderRow}>
              <TouchableOpacity
                onPress={() => setShowCurrencyModal(false)}
                testID="mapiCurrencyModalBack"
              >
                <BackIcon width={24} height={24} />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setShowCurrencyModal(false)}
                testID="mapiCurrencyModalClose"
              >
                <CloseIcon width={24} height={24} />
              </TouchableOpacity>
            </View>
            <Text style={styles.chooseCurrency}>
              {t("CHOOSE_CURRENCY_YOUR_RECEIVER_GETS")}
            </Text>
            {currencyOptions.map((opt) => (
              <TouchableOpacity
                key={opt.currency}
                style={styles.currencyModalOptionRow}
                onPress={() => handleCurrencySelect(opt)}
              >
                <Text style={styles.currencyOptionText}>{opt.currency}</Text>
                <View
                  style={[
                    styles.radioOuter,
                    selectedCurrency === opt.currency && styles.radioOuterSelected,
                  ]}
                >
                  {selectedCurrency === opt.currency && (
                    <RadioSelectedIcon width={20} height={20} />
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 16,
    paddingTop: 32,
  },
  title: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07,
  },
  youSendTitle: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 22,
    marginBottom: 6,
    marginTop: 48,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 6,
  },
  shrinkWrap: {
    flexShrink: 1,
    flexGrow: 0,
    alignSelf: "center",
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 40,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 46,
    textAlign: "center",
    minWidth: 60,
    maxWidth: 200,
    backgroundColor: "transparent",
    paddingVertical: 0,
    paddingHorizontal: 0,
    textAlignVertical: "center",
    includeFontPadding: false,
    ...({ outlineWidth: 0 } as any),
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 18,
  },
  subtitle: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  chooseCurrency: {
    fontFamily: "Rubik",
    fontSize: 24,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
    paddingRight: 50,
    marginBottom: 24,
  },
  currencyModalOptionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 32,
  },
  currencyOptionText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#D1D1D1",
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
});
